import {
	OrderType,
	OrderTriggerCondition,
	OrderStatus,
	PositionDirection,
	BigNum,
	ZERO,
	ContractTier,
	BN,
	PERCENTAGE_PRECISION,
	isOneOfVariant,
	MarketType,
	DriftClient,
	User,
	OrderParamsBitFlag,
} from '@drift-labs/sdk';
import {
	LIMIT_ORDER_TYPE_CONFIG,
	MARKET_ORDER_TYPE_CONFIG,
	ORACLE_LIMIT_ORDER_TYPE_CONFIG,
	ORACLE_MARKET_ORDER_TYPE_CONFIG,
	STOP_LIMIT_ORDER_TYPE_CONFIG,
	STOP_MARKET_ORDER_TYPE_CONFIG,
	TAKE_PROFIT_LIMIT_ORDER_TYPE_CONFIG,
	TAKE_PROFIT_MARKET_ORDER_TYPE_CONFIG,
	UI_ORDER_TYPES,
} from '../constants/orders';
import { UISerializableOrder } from '../serializableTypes';
import { ENUM_UTILS, matchEnum } from '../utils';
import { AuctionParams } from '../types';
import { EMPTY_AUCTION_PARAMS } from '../constants/trade';
import { MARKET_UTILS } from './market';

const getOrderLabelFromOrderDetails = (
	orderDetails: Pick<
		UISerializableOrder,
		| 'orderType'
		| 'oraclePriceOffset'
		| 'direction'
		| 'triggerCondition'
		| 'existingPositionDirection'
	>
) => {
	if (matchEnum(orderDetails.orderType, OrderType.MARKET))
		return UI_ORDER_TYPES.market.label;

	if (matchEnum(orderDetails.orderType, OrderType.LIMIT))
		return `${
			orderDetails.oraclePriceOffset &&
			!orderDetails.oraclePriceOffset?.eqZero()
				? 'Oracle '
				: ''
		}${UI_ORDER_TYPES.limit.label}`;

	if (matchEnum(orderDetails.orderType, OrderType.ORACLE))
		return UI_ORDER_TYPES.oracle.label;

	if (matchEnum(orderDetails.orderType, OrderType.TRIGGER_MARKET)) {
		const isTriggered =
			matchEnum(
				orderDetails.triggerCondition,
				OrderTriggerCondition.TRIGGERED_ABOVE
			) ||
			matchEnum(
				orderDetails.triggerCondition,
				OrderTriggerCondition.TRIGGERED_BELOW
			);
		if (isTriggered) {
			return 'Market (Triggered)';
		}

		const isTriggerAbove = matchEnum(
			orderDetails.triggerCondition,
			OrderTriggerCondition.ABOVE
		);
		const isOrderDirectionShort = matchEnum(
			orderDetails.direction,
			PositionDirection.SHORT
		);

		if (isTriggerAbove) {
			return isOrderDirectionShort
				? UI_ORDER_TYPES.takeProfitMarket.label
				: UI_ORDER_TYPES.stopMarket.label;
		} else {
			return isOrderDirectionShort
				? UI_ORDER_TYPES.stopMarket.label
				: UI_ORDER_TYPES.takeProfitMarket.label;
		}
	}

	if (matchEnum(orderDetails.orderType, OrderType.TRIGGER_LIMIT)) {
		const isTriggered =
			matchEnum(
				orderDetails.triggerCondition,
				OrderTriggerCondition.TRIGGERED_ABOVE
			) ||
			matchEnum(
				orderDetails.triggerCondition,
				OrderTriggerCondition.TRIGGERED_BELOW
			);

		const isTriggerAbove = matchEnum(
			orderDetails.triggerCondition,
			OrderTriggerCondition.ABOVE
		);
		const isOrderDirectionShort = matchEnum(
			orderDetails.direction,
			PositionDirection.SHORT
		);

		if (isTriggered) {
			return 'Limit (Triggered)';
		}

		if (isTriggerAbove) {
			return isOrderDirectionShort
				? UI_ORDER_TYPES.takeProfitLimit.label
				: UI_ORDER_TYPES.stopLimit.label;
		} else {
			return isOrderDirectionShort
				? UI_ORDER_TYPES.stopLimit.label
				: UI_ORDER_TYPES.takeProfitLimit.label;
		}
	}

	return '-';
};

const getLimitPriceFromOracleOffset = (
	order: UISerializableOrder,
	oraclePrice: BigNum
): BigNum => {
	if (
		(order.price && !order.price.eqZero()) ||
		!order.oraclePriceOffset ||
		order.oraclePriceOffset.eqZero() ||
		!oraclePrice ||
		oraclePrice?.eqZero()
	) {
		return order.price;
	}
	return oraclePrice.add(order.oraclePriceOffset);
};

function isAuctionEmpty(auctionParams: AuctionParams) {
	return (
		auctionParams.auctionStartPrice ===
			EMPTY_AUCTION_PARAMS.auctionStartPrice &&
		auctionParams.auctionEndPrice === EMPTY_AUCTION_PARAMS.auctionEndPrice &&
		auctionParams.auctionDuration === EMPTY_AUCTION_PARAMS.auctionDuration
	);
}

const getUIOrderTypeFromSdkOrderType = (
	orderType: OrderType,
	triggerCondition: OrderTriggerCondition,
	direction: PositionDirection,
	oracleOffset: BigNum | undefined
) => {
	const isLong = ENUM_UTILS.match(direction, PositionDirection.LONG);
	const triggerAbove =
		matchEnum(triggerCondition, OrderTriggerCondition.ABOVE) ||
		matchEnum(triggerCondition, OrderTriggerCondition.TRIGGERED_ABOVE);

	const triggerBelow =
		matchEnum(triggerCondition, OrderTriggerCondition.BELOW) ||
		matchEnum(triggerCondition, OrderTriggerCondition.TRIGGERED_BELOW);

	// Buy side + trigger below: take profit for a short position
	// Buy side + trigger above: stop loss for a short position
	// Sell side + trigger above: take profit for a long position
	// Sell side + trigger below: stop loss for a long position

	if (matchEnum(orderType, OrderType.MARKET)) {
		return MARKET_ORDER_TYPE_CONFIG;
	} else if (matchEnum(orderType, OrderType.LIMIT)) {
		if (oracleOffset && !oracleOffset.eq(ZERO)) {
			return ORACLE_LIMIT_ORDER_TYPE_CONFIG;
		} else {
			return LIMIT_ORDER_TYPE_CONFIG;
		}
	} else if (matchEnum(orderType, OrderType.TRIGGER_MARKET)) {
		if (isLong) {
			if (triggerAbove) {
				return STOP_MARKET_ORDER_TYPE_CONFIG;
			} else if (triggerBelow) {
				return TAKE_PROFIT_MARKET_ORDER_TYPE_CONFIG;
			}
		} else {
			if (triggerAbove) {
				return TAKE_PROFIT_MARKET_ORDER_TYPE_CONFIG;
			} else if (triggerBelow) {
				return STOP_MARKET_ORDER_TYPE_CONFIG;
			}
		}
	} else if (matchEnum(orderType, OrderType.TRIGGER_LIMIT)) {
		if (isLong) {
			if (triggerAbove) {
				return STOP_LIMIT_ORDER_TYPE_CONFIG;
			} else if (triggerBelow) {
				return TAKE_PROFIT_LIMIT_ORDER_TYPE_CONFIG;
			}
		} else {
			if (triggerAbove) {
				return TAKE_PROFIT_LIMIT_ORDER_TYPE_CONFIG;
			} else if (triggerBelow) {
				return STOP_LIMIT_ORDER_TYPE_CONFIG;
			}
		}
	} else if (matchEnum(orderType, OrderType.ORACLE)) {
		return ORACLE_MARKET_ORDER_TYPE_CONFIG;
	}
	throw new Error('Invalid order type');
};

function getPerpAuctionDuration(
	priceDiff: BN,
	price: BN,
	contractTier: ContractTier
): number {
	const percentDiff = priceDiff.mul(PERCENTAGE_PRECISION).div(price);

	const slotsPerBp = isOneOfVariant(contractTier, ['a', 'b'])
		? new BN(100)
		: new BN(60);

	const rawSlots = percentDiff
		.mul(slotsPerBp)
		.div(PERCENTAGE_PRECISION.divn(100));

	const clamped = BN.min(BN.max(rawSlots, new BN(5)), new BN(180));

	return clamped.toNumber();
}

export type HighLeverageOptions = {
	numOfOpenHighLeverageSpots?: number; // if not provided, the method will assume that there is spots open
	enterHighLeverageModeBufferPct?: number;
};

/**
 * Mainly checks if the user will be entering high leverage mode through this order.
 */
function getPerpOrderParamsBitFlags(
	marketIndex: number,
	driftClient: DriftClient,
	userAccount: User,
	attemptedLeverage: number,
	highLeverageOptions?: {
		numOfOpenHighLeverageSpots?: number; // if not provided, the method will assume that there is spots open
	}
): number | undefined {
	if (attemptedLeverage < 5) {
		// there is no high leverage mode leverage that is higher than 4
		return undefined;
	}

	const { numOfOpenHighLeverageSpots = Number.MAX_SAFE_INTEGER } =
		highLeverageOptions || {};

	if (numOfOpenHighLeverageSpots <= 0) {
		return undefined;
	}

	const {
		hasHighLeverage: isMarketAHighLeverageMarket,
		maxLeverage: regularMaxLeverage,
	} = MARKET_UTILS.getMaxLeverageForMarket(
		MarketType.PERP,
		marketIndex,
		driftClient
	);

	if (!isMarketAHighLeverageMarket) {
		return undefined;
	}

	// Check if user is already in high leverage mode
	if (userAccount.isHighLeverageMode('Initial')) {
		return undefined;
	}

	if (attemptedLeverage > regularMaxLeverage) {
		return OrderParamsBitFlag.UpdateHighLeverageMode;
	}

	return undefined;
}

const isOrderTriggered = (
	order: Pick<UISerializableOrder, 'orderType' | 'triggerCondition' | 'status'>
): boolean => {
	const isTriggerOrderType =
		ENUM_UTILS.match(order.orderType, OrderType.TRIGGER_MARKET) ||
		ENUM_UTILS.match(order.orderType, OrderType.TRIGGER_LIMIT);

	const orderWasCancelled = ENUM_UTILS.match(
		order.status,
		OrderStatus.CANCELED
	);

	const orderWasTriggered =
		ENUM_UTILS.match(
			order.triggerCondition,
			OrderTriggerCondition.TRIGGERED_ABOVE
		) ||
		ENUM_UTILS.match(
			order.triggerCondition,
			OrderTriggerCondition.TRIGGERED_BELOW
		);

	return isTriggerOrderType && orderWasTriggered && !orderWasCancelled;
};

export const ORDER_COMMON_UTILS = {
	getOrderLabelFromOrderDetails,
	getLimitPriceFromOracleOffset,
	isAuctionEmpty,
	getUIOrderTypeFromSdkOrderType,
	getPerpAuctionDuration,
	getPerpOrderParamsBitFlags,
	isOrderTriggered,
};
